import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { QuoteDraftLinePreview } from './types'

interface QuoteLineReorderDialogProps {
  line: QuoteDraftLinePreview | null
  totalLines: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onReorder: (newDisplayOrder: number) => void
}

export function QuoteLineReorderDialog({
  line,
  totalLines,
  open,
  onOpenChange,
  onReorder,
}: QuoteLineReorderDialogProps) {
  const currentDisplayOrder = typeof line?.displayOrder === 'number' ? line.displayOrder : null

  const initialValue = useMemo(() => {
    if (typeof currentDisplayOrder === 'number') return String(currentDisplayOrder)
    return ''
  }, [currentDisplayOrder])

  const [newPosition, setNewPosition] = useState(initialValue)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    /* eslint-disable react-hooks/set-state-in-effect */
    setNewPosition(initialValue)
    setError(null)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open, initialValue])

  const handleSubmit = () => {
    const position = parseInt(newPosition, 10)

    if (!Number.isFinite(position)) {
      setError('Veuillez entrer un nombre valide')
      return
    }

    if (position < 1 || (totalLines > 0 && position > totalLines)) {
      setError(`La position doit être entre 1 et ${totalLines}`)
      return
    }

    if (typeof currentDisplayOrder === 'number' && position === currentDisplayOrder) {
      setError('Cette position est déjà celle de la ligne')
      return
    }

    onReorder(position)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Changer l’ordre</DialogTitle>
          <DialogDescription>
            Déplacez "{line?.label ?? 'Ligne'}" vers une nouvelle position dans le devis.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right text-muted-foreground">Actuel</Label>
            <div className="col-span-3">
              <p className="font-medium">
                {typeof currentDisplayOrder === 'number' ? `${currentDisplayOrder} / ${totalLines}` : '-'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="position" className="text-right">
              Nouveau
            </Label>
            <div className="col-span-3 space-y-2">
              <Input
                id="position"
                type="number"
                min={1}
                max={totalLines || undefined}
                value={newPosition}
                onChange={(e) => {
                  setNewPosition(e.target.value)
                  setError(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit()
                }}
                className={error ? 'border-destructive' : ''}
                autoFocus
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <p className="text-xs text-muted-foreground">Entrez un nombre entre 1 et {totalLines}</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={!line}>
            Déplacer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
